import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import CloseIconBase from 'core/components/Icon/Close.svg';
import EmptyWrapper from 'core/components/EmptyWrapper';
import Portal from 'core/components/Portal';

import styles from './index.styl';

const ESC_KEY = 'Escape';

/**
 * Этот data атрибут :global(body[data-lb-fullscreen])
 * используется в компоненте Layout для скрытия всех элементов под модалку
 */
const MODAL_FULLSCREEN_INDICATOR = 'lbFullscreen';

class Modal extends Component {
  componentDidMount() {
    const { enableCloseOnEsc, isOpen, withoutPortal } = this.props;

    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (withoutPortal) {
      document.body.dataset[MODAL_FULLSCREEN_INDICATOR] = true;
    }

    if (enableCloseOnEsc) {
      window.addEventListener('keydown', this.handleKeyboard);
    }
  }

  componentWillUnmount() {
    const { enableCloseOnEsc, withoutPortal } = this.props;
    document.body.style.overflow = '';

    if (withoutPortal) {
      delete document.body.dataset[MODAL_FULLSCREEN_INDICATOR];
    }

    if (enableCloseOnEsc) {
      window.removeEventListener('keydown', this.handleKeyboard);
    }
  }

  handleKeyboard = ({ key }) => {
    const { onClose, isOpen } = this.props;

    if (isOpen && key === ESC_KEY && onClose) {
      onClose();
    }
  };

  render() {
    const {
      isOpen,
      children,
      onClose,
      hideCloseBtn,
      scopedStyles,
      closeIcon: CloseIconCustom,
      withoutPortal,
    } = this.props;

    const CloseIcon = CloseIconCustom || CloseIconBase;

    if (!isOpen) return null;

    const Wrapper = withoutPortal ? EmptyWrapper : Portal;

    return (
      <Fragment>
        <Wrapper>
          <div className={styles.container} data-qa='modal'>
            <div
              className={cx(
                styles.modalOverlay,
                scopedStyles && scopedStyles.wrapClassNames('modalOverlay')
              )}
            >
              <div
                className={cx(
                  styles.modalWindow,
                  scopedStyles && scopedStyles.wrapClassNames('modalWindow')
                )}
              >
                {!hideCloseBtn && (
                  <div
                    data-qa='modal-close'
                    onClick={onClose}
                    className={cx(
                      styles.closeContainer,
                      scopedStyles && scopedStyles.wrapClassNames('closeContainer')
                    )}
                  >
                    <CloseIcon
                      className={cx(
                        styles.closeIcon,
                        scopedStyles && scopedStyles.wrapClassNames('closeIcon')
                      )}
                    />
                  </div>
                )}
                {children}
              </div>
            </div>
          </div>
        </Wrapper>
        {scopedStyles && <scopedStyles.styles />}
      </Fragment>
    );
  }
}

Modal.defaultProps = {
  isOpen: false,
  onClose: () => null,
  hideCloseBtn: false,
};

Modal.propTypes = {
  /**
   * Стилизация элементов модалки, с помощью scope-классов.
   *
   * Поддерживаемые classNames:
   * - `.modalOverlay`
   * - `.modalWindow`
   * - `.closeContainer`
   * - `.closeIcon`
   */
  scopedStyles: PropTypes.object,
  /** Контент модалки */
  children: PropTypes.node,
  /** Признак открытой/закрытой модалки */
  isOpen: PropTypes.bool,
  /** Обработчик закрытия модалки */
  onClose: PropTypes.func,
  /** Можно скрыть кнопку закрытия окна */
  hideCloseBtn: PropTypes.bool,
  /** Кастомная иконка закрытия модалки */
  closeIcon: PropTypes.func,
  /**
   * По умолчанию Modal отрисовывается с помощью Portal.
   * Для JS с отложенной инициализацией, Portal может привести к фатальным последствиям.
   * Например Swiper не инициализируется c defaultItemIndex.
   */
  withoutPortal: PropTypes.bool,
  /** Включает возможность закрытия модалки нажатием ESC */
  enableCloseOnEsc: PropTypes.bool,
};

export default Modal;
